import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_URL } from '../utils/Functions';

const AdminForms = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  // Drawer State
  const [showDrawer, setShowDrawer] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    name: '', slug: '', description: '', schema: '[\n  {\n    "name": "field_1",\n    "label": "Example Field",\n    "type": "text",\n    "required": true\n  }\n]', is_active: true, has_captcha: false
  });

  const [saving, setSaving] = useState(false);

  const FIELD_TYPES = [
    { value: 'text',     label: 'Text Input',      icon: 'fa-font' },
    { value: 'email',    label: 'Email',            icon: 'fa-envelope' },
    { value: 'tel',      label: 'Phone Number',     icon: 'fa-phone' },
    { value: 'number',   label: 'Number',           icon: 'fa-hashtag' },
    { value: 'textarea', label: 'Text Area',        icon: 'fa-align-left' },
    { value: 'select',   label: 'Dropdown',         icon: 'fa-caret-down' },
    { value: 'radio',    label: 'Radio Buttons',    icon: 'fa-circle-dot' },
    { value: 'checkbox', label: 'Checkbox',         icon: 'fa-square-check' },
    { value: 'date',     label: 'Date Picker',      icon: 'fa-calendar' },
    { value: 'file',     label: 'File Upload',      icon: 'fa-upload' },
  ];

  const [schemaFields, setSchemaFields] = useState([]);

  const buildEmptyField = () => ({
    _id: Date.now() + Math.random(),
    name: '',
    label: '',
    type: '',
    placeholder: '',
    required: false,
    options: '',   // comma-separated for select/radio
  });

  const fetchForms = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/forms`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      const res = await response.json();
      if (!response.ok) throw res;
      setForms(res.data?.data || res.data || []);
    } catch (err) {
      setError('Failed to load forms.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForms();
  }, []);

  const handleOpenCreate = () => {
    setFormData({ name: '', slug: '', description: '', is_active: true, has_captcha: false });
    setSchemaFields([buildEmptyField()]);
    setIsEditing(false);
    setCurrentId(null);
    setShowDrawer(true);
  };

  const handleOpenEdit = (form) => {
    setFormData({
      name: form.name || '',
      slug: form.slug || '',
      description: form.description || '',
      is_active: !!form.is_active,
      has_captcha: !!form.has_captcha,
    });
    const existing = (form.schema || []).map(f => ({
      _id: Date.now() + Math.random(),
      name: f.name || '',
      label: f.label || '',
      type: f.type || 'text',
      placeholder: f.placeholder || '',
      required: !!f.required,
      options: Array.isArray(f.options) ? f.options.join(', ') : (f.options || ''),
    }));
    setSchemaFields(existing.length ? existing : [buildEmptyField()]);
    setIsEditing(true);
    setCurrentId(form.id);
    setShowDrawer(true);
  };

  const confirmDelete = (form) => {
    setItemToDelete(form);
  };

  const executeDelete = async () => {
    if (!itemToDelete) return;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/forms/${itemToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to delete');
      setForms(forms.filter(f => f.id !== itemToDelete.id));
      setItemToDelete(null);
      toast.success('Form deleted successfully!');
    } catch (err) {
      toast.error('Error deleting form.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const parsedSchema = schemaFields
        .filter(f => f.type && f.name)
        .map(f => {
          const field = { name: f.name, label: f.label, type: f.type, required: f.required };
          if (f.placeholder) field.placeholder = f.placeholder;
          if ((f.type === 'select' || f.type === 'radio') && f.options)
            field.options = f.options.split(',').map(o => o.trim()).filter(Boolean);
          return field;
        });

      const token = localStorage.getItem('auth_token');
      const url = isEditing ? `${API_URL}/api/admin/forms/${currentId}` : `${API_URL}/api/admin/forms`;
      const method = isEditing ? 'PUT' : 'POST';
      const payload = { ...formData, schema: parsedSchema };

      const response = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      const res = await response.json();
      if (!response.ok) throw res;
      setShowDrawer(false);
      fetchForms();
      toast.success(isEditing ? 'Form updated successfully!' : 'Form created successfully!');
    } catch (err) {
      toast.error(err.message || 'Error saving form.');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (id, key, value) => {
    setSchemaFields(prev => prev.map(f => f._id === id ? { ...f, [key]: value } : f));
  };

  const addField = () => {
    setSchemaFields(prev => [...prev, buildEmptyField()]);
  };

  const removeField = (id) => {
    setSchemaFields(prev => prev.filter(f => f._id !== id));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'name') {
      const autoSlug = value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
      setFormData(prev => ({ ...prev, name: value, slug: autoSlug }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const filteredForms = forms.filter(form => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (form.name && form.name.toLowerCase().includes(term)) ||
      (form.slug && form.slug.toLowerCase().includes(term)) ||
      (form.description && form.description.toLowerCase().includes(term))
    );
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .fm-wrap { font-family: 'Inter', sans-serif; }

        /* Hero Banner */
        .fm-hero {
          background: linear-gradient(135deg, #040f1f 0%, #061830 40%, #CFA047 100%);
          border-radius: 18px; padding: 32px 36px; margin-bottom: 28px;
          display: flex; align-items: center; justify-content: space-between;
          position: relative; overflow: hidden;
        }
        .fm-hero::before { content: ''; position: absolute; top: -40px; right: -40px; width: 240px; height: 240px; border-radius: 50%; background: rgba(255,255,255,0.05); }
        .fm-hero::after { content: ''; position: absolute; bottom: -60px; right: 120px; width: 180px; height: 180px; border-radius: 50%; background: rgba(255,255,255,0.04); }
        .fm-hero-left { position: relative; z-index: 1; }
        .fm-hero-eyebrow { font-size: 11px; font-weight: 700; color: #CFA047; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .fm-hero-title { font-size: 26px; font-weight: 800; color: #ffffff; margin: 0 0 8px; line-height: 1.2; }
        .fm-hero-sub { font-size: 14px; color: rgba(255,255,255,0.8); margin: 0; }
        .fm-hero-right { position: relative; z-index: 1; display: flex; align-items: center; gap: 16px; }
        .fm-hero-stat { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.15); border-radius: 14px; padding: 16px 22px; text-align: center; backdrop-filter: blur(6px); }
        .fm-hero-stat-val { font-size: 28px; font-weight: 800; color: #ffffff; line-height: 1; }
        .fm-hero-stat-label { font-size: 11px; color: #CFA047; font-weight: 600; margin-top: 4px; }
        .fm-create-btn { display: flex; align-items: center; gap: 8px; padding: 11px 22px; border-radius: 10px; border: none; background: #ffffff; color: #061830; font-size: 14px; font-weight: 700; cursor: pointer; font-family: 'Inter', sans-serif; box-shadow: 0 4px 14px rgba(0,0,0,0.15); transition: all 0.2s; white-space: nowrap; }
        .fm-create-btn:hover { box-shadow: 0 6px 18px rgba(207, 160, 71,0.4); transform: translateY(-1px); }

        /* Error */
        .fm-error { background: #fff1f2; border: 1px solid #fecdd3; border-radius: 10px; padding: 12px 16px; color: #e11d48; font-size: 13px; margin-bottom: 20px; display: flex; align-items: center; gap: 10px; }

        /* Table card */
        .fm-table-card { background: #ffffff; border-radius: 14px; border: 1px solid #dde3ea; box-shadow: 0 2px 12px rgba(0,0,0,0.05); overflow: hidden; }
        .fm-table-toolbar { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid #eef1f5; flex-wrap: wrap; gap: 12px; }
        .fm-table-toolbar-title { font-size: 14px; font-weight: 600; color: #2d3a4b; }
        .fm-count-badge { background: #f0f4f8; color: #5a6a7e; font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 20px; margin-left: 8px; }
        .fm-search-box { position: relative; width: 280px; }
        .fm-search-box i { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #9babbf; font-size: 13px; }
        .fm-search-box input { width: 100%; padding: 10px 14px 10px 38px; border-radius: 10px; border: 1.5px solid #dde3ea; font-size: 13px; font-family: 'Inter', sans-serif; color: #2d3a4b; outline: none; transition: all 0.2s; background: #f9fbfc; box-shadow: 0 1px 3px rgba(0,0,0,0.02); }
        .fm-search-box input:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207, 160, 71,0.12); background: #fff; }
        table.fm-table { width: 100%; border-collapse: collapse; }
        .fm-table thead tr { background: #f7f9fc; }
        .fm-table th { padding: 13px 18px; font-size: 11.5px; font-weight: 700; color: #8496a9; text-transform: uppercase; letter-spacing: 0.6px; border-bottom: 1px solid #eef1f5; }
        .fm-table td { padding: 15px 18px; font-size: 13.5px; color: #3d4f62; border-bottom: 1px solid #f0f4f8; vertical-align: middle; }
        .fm-table tbody tr:last-child td { border-bottom: none; }
        .fm-table tbody tr:hover td { background: #f7f9fc; }

        .fm-badge { display: inline-flex; align-items: center; gap: 5px; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
        .fm-badge.active { background: #f8fafc; color: #061830; }
        .fm-badge.inactive { background: #f8fafc; color: #94a3b8; }
        
        .fm-actions { display: flex; gap: 6px; }
        .fm-act-btn { display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 8px; border: 1.5px solid #dde3ea; background: #f7f9fc; color: #6b7a90; cursor: pointer; font-size: 13px; transition: all 0.18s; }
        .fm-act-btn:hover { background: #fff; border-color: #bcc7d4; color: #1a2332; }
        .fm-act-btn.edit:hover { border-color: #CFA047; color: #CFA047; background: #f8fafc; }
        .fm-act-btn.del:hover { border-color: #fca5a5; color: #ef4444; background: #fff1f2; }
        .fm-act-btn.submissions:hover { border-color: #93c5fd; color: #3b82f6; background: #eff6ff; }

        /* Slide-in Drawer */
        .fm-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); backdrop-filter: blur(4px); z-index: 900; display: flex; justify-content: flex-end; }
        @keyframes fm-drop { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .fm-drawer { width: 560px; max-width: 95vw; background: #fff; height: 100vh; display: flex; flex-direction: column; box-shadow: -8px 0 40px rgba(0,0,0,0.18); animation: fm-drop 0.28s cubic-bezier(0.22,1,0.36,1); }
        .fm-drawer-hd { padding: 22px 28px; border-bottom: 1px solid #eef1f5; display: flex; align-items: center; justify-content: space-between; background: linear-gradient(135deg, #061830, #040f1f); flex-shrink: 0; }
        .fm-drawer-hd-left { display: flex; align-items: center; gap: 14px; }
        .fm-drawer-icon { width: 40px; height: 40px; border-radius: 12px; background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25); display: flex; align-items: center; justify-content: center; color: #CFA047; font-size: 16px; }
        .fm-drawer-title { font-size: 17px; font-weight: 700; color: #fff; margin: 0; }
        .fm-drawer-subtitle { font-size: 12px; color: rgba(255,255,255,0.8); margin-top: 2px; }
        .fm-drawer-close { background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.2); color: white; width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 14px; transition: background 0.18s; }
        .fm-drawer-close:hover { background: rgba(255,255,255,0.22); }

        .fm-form-panel { flex: 1; overflow-y: auto; padding: 28px; display: flex; flex-direction: column; gap: 20px; }

        .fm-section-title { font-size: 11px; font-weight: 700; color: #CFA047; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 14px; display: flex; align-items: center; gap: 8px; }
        .fm-section-title::after { content: ''; flex: 1; height: 1px; background: #fdf5e6; }

        .fm-float-group { position: relative; margin-bottom: 6px; }
        .fm-float-inp { width: 100%; padding: 20px 16px 8px; border-radius: 10px; border: 1.5px solid #dde3ea; font-size: 14px; font-family: 'Inter', sans-serif; color: #2d3a4b; outline: none; background: #f9fbfc; transition: border-color 0.18s, box-shadow 0.18s; box-sizing: border-box; }
        .fm-float-inp:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207, 160, 71,0.1); background: #fff; }
        .fm-float-inp:focus + .fm-float-label, .fm-float-inp:not(:placeholder-shown) + .fm-float-label { top: 7px; font-size: 10.5px; color: #CFA047; font-weight: 700; }
        .fm-float-label { position: absolute; top: 14px; left: 16px; font-size: 13px; color: #9babbf; font-weight: 500; pointer-events: none; transition: all 0.18s; }
        .fm-float-textarea { min-height: 100px; resize: vertical; line-height: 1.6; padding-top: 22px; }
        .fm-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .fm-hint { font-size: 11.5px; color: #9babbf; margin-top: 4px; padding-left: 4px; }
        
        .code-editor { font-family: 'Courier New', Courier, monospace; background: #1e293b; color: #e2e8f0; min-height: 200px; padding: 16px; border-radius: 10px; border: 1px solid #334155; width: 100%; font-size: 13px; line-height: 1.5; box-sizing: border-box; }
        .code-editor:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.2); }

        .fm-switch-row { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; background: #f9fbfc; border: 1.5px solid #dde3ea; border-radius: 10px; }
        .fm-switch-label { font-size: 13px; font-weight: 600; color: #2d3a4b; }
        .fm-switch-sub { font-size: 11px; color: #9babbf; margin-top: 2px; }
        .fm-switch { position: relative; width: 40px; height: 24px; flex-shrink: 0; }
        .fm-switch input { opacity: 0; width: 0; height: 0; }
        .fm-slider { position: absolute; inset: 0; background: #dde3ea; border-radius: 50px; cursor: pointer; transition: 0.2s; }
        .fm-slider:before { content: ''; position: absolute; width: 18px; height: 18px; left: 3px; top: 3px; background: white; border-radius: 50%; transition: 0.2s; box-shadow: 0 1px 4px rgba(0,0,0,0.18); }
        .fm-switch input:checked + .fm-slider { background: #CFA047; }
        .fm-switch input:checked + .fm-slider:before { transform: translateX(16px); }

        .fm-df { padding: 18px 28px; border-top: 1px solid #eef1f5; display: flex; justify-content: space-between; align-items: center; background: #f9fbfc; flex-shrink: 0; }
        .fm-df-hint { font-size: 12px; color: #9babbf; }
        .fm-df-btns { display: flex; gap: 12px; }
        .fm-btn-cancel { padding: 10px 22px; border-radius: 9px; font-weight: 600; cursor: pointer; background: white; border: 1.5px solid #dde3ea; color: #4a5568; font-family: 'Inter', sans-serif; font-size: 13px; transition: all 0.18s; }
        .fm-btn-cancel:hover { background: #f7f9fc; }
        .fm-btn-save { padding: 10px 26px; border-radius: 9px; font-weight: 600; cursor: pointer; background: linear-gradient(135deg, #CFA047 0%, #061830 100%); border: none; color: white; font-family: 'Inter', sans-serif; font-size: 13px; box-shadow: 0 2px 8px rgba(207, 160, 71,0.3); transition: all 0.18s; display: flex; align-items: center; gap: 8px; }
        .fm-btn-save:hover { box-shadow: 0 4px 14px rgba(207, 160, 71,0.4); transform: translateY(-1px); }
        .fm-btn-save:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

        .fm-del-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.6); backdrop-filter: blur(4px); display: flex; justify-content: center; align-items: center; z-index: 999; }
        @keyframes fm-slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

        /* Visual Schema Builder */
        .fb-field-card { border: 1.5px solid #e8edf4; border-radius: 12px; background: #f9fbfc; overflow: hidden; transition: border-color 0.2s; }
        .fb-field-card:hover { border-color: #c8d4e0; }
        .fb-field-card-head { display: flex; align-items: center; gap: 10px; padding: 12px 14px; border-bottom: 1px solid #eef1f5; background: #fff; }
        .fb-field-num { width: 24px; height: 24px; border-radius: 6px; background: linear-gradient(135deg, #061830, #CFA047); color: white; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .fb-field-type-sel { flex: 1; border: 1.5px solid #e0e7ef; border-radius: 8px; padding: 8px 12px; font-size: 13px; font-family: 'Inter', sans-serif; color: #2d3a4b; background: #f9fbfc; outline: none; cursor: pointer; transition: border-color 0.18s; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%236b7a90' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 10px center; background-size: 14px; padding-right: 32px; }
        .fb-field-type-sel:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207,160,71,0.1); }
        .fb-field-del { width: 30px; height: 30px; border-radius: 7px; border: 1.5px solid #fca5a5; background: #fff1f2; color: #ef4444; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 12px; transition: all 0.18s; flex-shrink: 0; }
        .fb-field-del:hover { background: #ef4444; color: white; border-color: #ef4444; }
        .fb-field-body { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; padding: 14px; }
        .fb-field-body.full-col { grid-template-columns: 1fr; }
        .fb-field-inp { width: 100%; padding: 9px 12px; border: 1.5px solid #e0e7ef; border-radius: 8px; font-size: 13px; font-family: 'Inter', sans-serif; color: #2d3a4b; background: #ffffff; outline: none; transition: border-color 0.18s; box-sizing: border-box; }
        .fb-field-inp:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207,160,71,0.1); }
        .fb-field-label { font-size: 11px; font-weight: 600; color: #7a8fa6; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.4px; }
        .fb-req-row { display: flex; align-items: center; gap: 8px; grid-column: 1/-1; }
        .fb-req-row input[type="checkbox"] { width: 15px; height: 15px; accent-color: #CFA047; cursor: pointer; }
        .fb-req-row label { font-size: 12.5px; color: #4a5568; font-weight: 500; cursor: pointer; }
        .fb-add-btn { display: flex; align-items: center; gap: 8px; justify-content: center; width: 100%; padding: 11px; border-radius: 10px; border: 2px dashed #c8d4e0; background: transparent; color: #6b7a90; font-size: 13px; font-weight: 600; font-family: 'Inter', sans-serif; cursor: pointer; transition: all 0.2s; margin-top: 4px; }
        .fb-add-btn:hover { border-color: #CFA047; color: #CFA047; background: #fdf5e6; }
        .fb-empty-hint { text-align: center; padding: 24px; color: #9babbf; font-size: 13px; }
      `}</style>

      <div className="fm-wrap">
        {/* Hero Banner */}
        <div className="fm-hero">
          <div className="fm-hero-left">
            <div className="fm-hero-eyebrow"><i className="fa-solid fa-table-list"></i> Forms Management</div>
            <h2 className="fm-hero-title">Custom Forms Builder</h2>
            <p className="fm-hero-sub">Create and manage custom data collection forms for your clients.</p>
          </div>
          <div className="fm-hero-right">
            {!loading && (
              <>
                <div className="fm-hero-stat">
                  <div className="fm-hero-stat-val">{forms.length}</div>
                  <div className="fm-hero-stat-label">Total</div>
                </div>
                <div className="fm-hero-stat">
                  <div className="fm-hero-stat-val">{forms.filter(f => f.is_active).length}</div>
                  <div className="fm-hero-stat-label">Active</div>
                </div>
              </>
            )}
            <button className="fm-create-btn" onClick={handleOpenCreate}>
              <i className="fa-solid fa-plus"></i>
              Create Form
            </button>
          </div>
        </div>

        {error && <div className="fm-error"><i className="fa-solid fa-circle-exclamation"></i>{error}</div>}

        <div className="fm-table-card">
          <div className="fm-table-toolbar">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span className="fm-table-toolbar-title">All Forms</span>
              <span className="fm-count-badge">{filteredForms.length}</span>
            </div>
            <div className="fm-search-box">
              <i className="fa-solid fa-magnifying-glass"></i>
              <input 
                type="text" 
                placeholder="Search forms by name or slug..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
              />
            </div>
          </div>
          <table className="fm-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Fields Count</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#9babbf' }}>Loading forms...</td></tr>
              ) : forms.length === 0 ? (
                <tr><td colSpan="5" style={{ textAlign: 'center', padding: '60px', color: '#9babbf' }}>No forms yet. Click "Create Form" to add your first one.</td></tr>
              ) : filteredForms.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '60px' }}>
                    <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: '#cbd5e1', fontSize: '24px' }}>
                      <i className="fa-solid fa-magnifying-glass"></i>
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b', marginBottom: '8px' }}>No matching forms</div>
                    <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>We couldn't find any forms matching "{searchTerm}".</div>
                    <button onClick={() => setSearchTerm('')} style={{ background: '#fff', border: '1px solid #dde3ea', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, color: '#475569' }}>Clear Search</button>
                  </td>
                </tr>
              ) : (
                filteredForms.map(form => (
                  <tr key={form.id}>
                    <td><span style={{ fontWeight: 600, color: '#1a2332' }}>{form.name}</span></td>
                    <td><code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontSize: '13px', color: '#475569' }}>/{form.slug}</code></td>
                    <td><span style={{ color: '#64748b' }}>{form.schema ? form.schema.length : 0} fields</span></td>
                    <td>
                      <span className={`fm-badge ${form.is_active ? 'active' : 'inactive'}`}>
                        {form.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="fm-actions" style={{ justifyContent: 'flex-end' }}>
                        <button className="fm-act-btn submissions" onClick={() => navigate(`/admin/forms/${form.id}/submissions`)} title="View Submissions"><i className="fa-solid fa-list-check"></i></button>
                        <button className="fm-act-btn edit" onClick={() => handleOpenEdit(form)} title="Edit"><i className="fa-solid fa-pen-to-square"></i></button>
                        <button className="fm-act-btn del" onClick={() => confirmDelete(form)} title="Delete"><i className="fa-solid fa-trash-can"></i></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Slide-in Drawer */}
      {showDrawer && (
        <div className="fm-overlay" onClick={() => setShowDrawer(false)}>
          <div className="fm-drawer" onClick={e => e.stopPropagation()}>
            <div className="fm-drawer-hd">
              <div className="fm-drawer-hd-left">
                <div className="fm-drawer-icon"><i className="fa-solid fa-table-list"></i></div>
                <div>
                  <div className="fm-drawer-title">{isEditing ? 'Edit Form' : 'Create New Form'}</div>
                  <div className="fm-drawer-subtitle">{isEditing ? 'Update the form structure below' : 'Build a new data collection form'}</div>
                </div>
              </div>
              <button className="fm-drawer-close" onClick={() => setShowDrawer(false)}><i className="fa-solid fa-xmark"></i></button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="fm-form-panel">
                <div>
                  <div className="fm-section-title"><i className="fa-solid fa-info-circle"></i> Form Information</div>
                  <div className="fm-float-group">
                    <input className="fm-float-inp" type="text" name="name" value={formData.name} onChange={handleChange} placeholder=" " required />
                    <label className="fm-float-label">Form Name *</label>
                  </div>

                  <div className="fm-float-group" style={{ marginTop: '12px' }}>
                    <input className="fm-float-inp" type="text" name="slug" value={formData.slug} onChange={handleChange} placeholder=" " required />
                    <label className="fm-float-label">Slug * (e.g. tax-assessment)</label>
                  </div>

                  <div className="fm-float-group" style={{ marginTop: '12px' }}>
                    <textarea className="fm-float-inp fm-float-textarea" name="description" value={formData.description} onChange={handleChange} placeholder=" "></textarea>
                    <label className="fm-float-label">Description</label>
                  </div>

                  <div className="fm-float-group" style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" name="has_captcha" checked={formData.has_captcha} onChange={handleChange} id="has_captcha" />
                    <label htmlFor="has_captcha" style={{ cursor: 'pointer', fontSize: '14px', color: '#4a5568' }}>Enable CAPTCHA for this form</label>
                  </div>
                </div>

                <div>
                  <div className="fm-section-title"><i className="fa-solid fa-list-check"></i> Form Fields</div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {schemaFields.length === 0 && (
                      <div className="fb-empty-hint">No fields yet. Click "+ Add Field" below to start building.</div>
                    )}

                    {schemaFields.map((field, idx) => (
                      <div key={field._id} className="fb-field-card">
                        {/* Header: number + type dropdown + delete */}
                        <div className="fb-field-card-head">
                          <div className="fb-field-num">{idx + 1}</div>
                          <select
                            className="fb-field-type-sel"
                            value={field.type}
                            onChange={e => updateField(field._id, 'type', e.target.value)}
                          >
                            <option value="">— Select Field Type —</option>
                            {FIELD_TYPES.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                          <button type="button" className="fb-field-del" onClick={() => removeField(field._id)} title="Remove field">
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>

                        {/* Config rows only shown after type is selected */}
                        {field.type && (
                          <div className={`fb-field-body${field.type === 'select' || field.type === 'radio' ? '' : ''}`}>
                            <div>
                              <div className="fb-field-label">Field Name (key)</div>
                              <input
                                className="fb-field-inp"
                                type="text"
                                placeholder="e.g. full_name"
                                value={field.name}
                                onChange={e => updateField(field._id, 'name', e.target.value.replace(/\s+/g, '_').toLowerCase())}
                              />
                            </div>
                            <div>
                              <div className="fb-field-label">Label</div>
                              <input
                                className="fb-field-inp"
                                type="text"
                                placeholder="e.g. Full Name"
                                value={field.label}
                                onChange={e => updateField(field._id, 'label', e.target.value)}
                              />
                            </div>

                            {!['checkbox', 'date', 'file', 'radio'].includes(field.type) && (
                              <div style={{ gridColumn: '1/-1' }}>
                                <div className="fb-field-label">Placeholder</div>
                                <input
                                  className="fb-field-inp"
                                  type="text"
                                  placeholder="Optional hint text..."
                                  value={field.placeholder}
                                  onChange={e => updateField(field._id, 'placeholder', e.target.value)}
                                />
                              </div>
                            )}

                            {(field.type === 'select' || field.type === 'radio') && (
                              <div style={{ gridColumn: '1/-1' }}>
                                <div className="fb-field-label">Options <span style={{ color: '#9babbf', fontWeight: 400, textTransform: 'none' }}>(comma-separated)</span></div>
                                <input
                                  className="fb-field-inp"
                                  type="text"
                                  placeholder="Option A, Option B, Option C"
                                  value={field.options}
                                  onChange={e => updateField(field._id, 'options', e.target.value)}
                                />
                              </div>
                            )}

                            <div className="fb-req-row">
                              <input
                                type="checkbox"
                                id={`req-${field._id}`}
                                checked={field.required}
                                onChange={e => updateField(field._id, 'required', e.target.checked)}
                              />
                              <label htmlFor={`req-${field._id}`}>Required field</label>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    <button type="button" className="fb-add-btn" onClick={addField}>
                      <i className="fa-solid fa-plus"></i> Add Field
                    </button>
                  </div>
                </div>

                <div>
                  <div className="fm-section-title"><i className="fa-solid fa-gear"></i> Settings</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div className="fm-switch-row">
                      <div>
                        <div className="fm-switch-label">Active</div>
                        <div className="fm-switch-sub">Form is open for submissions</div>
                      </div>
                      <label className="fm-switch">
                        <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} />
                        <span className="fm-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="fm-df">
                <span className="fm-df-hint"><i className="fa-solid fa-asterisk" style={{ fontSize: '9px', verticalAlign: 'middle' }}></i> Required fields</span>
                <div className="fm-df-btns">
                  <button type="button" className="fm-btn-cancel" onClick={() => setShowDrawer(false)}>Cancel</button>
                  <button type="submit" className="fm-btn-save" disabled={saving}>
                    {saving ? <><i className="fa-solid fa-spinner fa-spin"></i> Saving…</> : <><i className="fa-solid fa-floppy-disk"></i> {isEditing ? 'Update Form' : 'Save Form'}</>}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {itemToDelete && (
        <div className="fm-del-overlay" onClick={() => setItemToDelete(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', padding: '28px 24px', borderRadius: '16px', width: '320px', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', animation: 'fm-slide-up 0.2s ease-out' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', margin: '0 auto 16px' }}>
              <i className="fa-solid fa-triangle-exclamation"></i>
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>Delete Form</h3>
            <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 24px', lineHeight: 1.5 }}>
              Are you sure you want to delete <strong>"{itemToDelete.name}"</strong>? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button style={{ flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#f1f5f9', color: '#475569' }} onClick={() => setItemToDelete(null)}>Cancel</button>
              <button style={{ flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#ef4444', color: 'white' }} onClick={executeDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminForms;
